import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { RestaurantAuthProvider } from './contexts/RestaurantAuthContext';
import { CustomerProvider } from './contexts/CustomerContext';
import AddressGate from './components/customer/AddressGate';
import { customerSupabase } from './customerSupabase';
import { getOrCreateCustomerProfile, isCustomerProfileComplete } from './utils/customerProfile';

// Critical path - loaded immediately (customer landing)
import CustomerDashboard from './pages/CustomerDashboard';

// Lazy-loaded routes - only fetched when navigated to
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const ManagePartners = lazy(() => import('./pages/ManagePartners'));
const ManageCoupons = lazy(() => import('./pages/ManageCoupons'));
const ManageRestaurants = lazy(() => import('./pages/ManageRestaurants'));
const ManageMenuItems = lazy(() => import('./pages/ManageMenuItems'));
const RestaurantMenuPage = lazy(() => import('./pages/RestaurantMenuPage'));
const MenuItemDetailPage = lazy(() => import('./pages/MenuItemDetailPage'));
const CustomerLogin = lazy(() => import('./pages/CustomerLogin'));
const CustomerRegister = lazy(() => import('./pages/CustomerRegister'));
const EditProfile = lazy(() => import('./pages/EditProfile'));
const UpdateProfile = lazy(() => import('./pages/UpdateProfile'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const WishlistPage = lazy(() => import('./pages/WishlistPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const PartnerLogin = lazy(() => import('./pages/PartnerLogin'));
const PartnerDashboard = lazy(() => import('./pages/PartnerDashboard'));
const OrdersManagement = lazy(() => import('./pages/OrdersManagement'));
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics'));
const AdminOrdersEnhanced = lazy(() => import('./pages/AdminOrdersEnhanced'));
const CustomerOrders = lazy(() => import('./pages/CustomerOrders'));
const RestaurantLogin = lazy(() => import('./pages/RestaurantLogin'));
const RestaurantPortalDashboard = lazy(() => import('./pages/RestaurantPortalDashboard'));
const ViewAllPage = lazy(() => import('./pages/ViewAllPage'));
const FastFoodsPage = lazy(() => import('./pages/FastFoodsPage'));
const TravelPage = lazy(() => import('./pages/TravelPage'));
const CategoriesPage = lazy(() => import('./pages/CategoriesPage'));
const FoodsPage = lazy(() => import('./pages/FoodsPage'));
const DeliveriesPage = lazy(() => import('./pages/DeliveriesPage'));
const DownloadPage = lazy(() => import('./pages/DownloadPage'));


// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-black mx-auto mb-3"></div>
      <p className="text-gray-400 text-sm">Loading...</p>
    </div>
  </div>
);

// AuthCallback handles OAuth redirects - waits for Supabase to pick up the session from URL
function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const consumeRedirect = () => {
      const target = localStorage.getItem('postLoginRedirect');
      localStorage.removeItem('postLoginRedirect');
      return target || '/customer/deliveries';
    };

    const handleAuth = async () => {
      try {
        // Wait for Supabase to process the tokens from the URL hash
        // detectSessionInUrl: true in the client config handles this automatically
        // but we need to give it time and then read the result
        const { data: { session }, error: sessionError } = await customerSupabase.auth.getSession();

        if (sessionError) {
          console.error('Auth callback error:', sessionError);
          setError(sessionError.message);
          return;
        }

        if (session?.user) {
          localStorage.setItem('userId', session.user.id);
          localStorage.setItem('userRole', 'customer');

          // Check if profile is complete
          const profile = await getOrCreateCustomerProfile(session.user);
          if (!isCustomerProfileComplete(profile)) {
            if (mounted) {
              navigate('/customer/update-profile', {
                replace: true,
                state: {
                  notice: {
                    variant: 'info',
                    title: 'Complete your profile',
                    message: 'Please continue the details to proceed.'
                  }
                }
              });
            }
            return;
          }

          if (mounted) {
            navigate(consumeRedirect(), { replace: true });
          }
        } else {
          // No session found - possibly tokens expired or invalid
          // Try listening for the auth state change (tokens might still be processing)
          const { data: { subscription } } = customerSupabase.auth.onAuthStateChange(
            async (event, session) => {
              if (event === 'SIGNED_IN' && session?.user) {
                localStorage.setItem('userId', session.user.id);
                localStorage.setItem('userRole', 'customer');

                const profile = await getOrCreateCustomerProfile(session.user);
                if (!isCustomerProfileComplete(profile)) {
                  if (mounted) {
                    navigate('/customer/update-profile', {
                      replace: true,
                      state: {
                        notice: {
                          variant: 'info',
                          title: 'Complete your profile',
                          message: 'Please continue the details to proceed.'
                        }
                      }
                    });
                  }
                } else {
                  if (mounted) {
                    navigate(consumeRedirect(), { replace: true });
                  }
                }
                subscription.unsubscribe();
              }
            }
          );

          // Timeout - if no session after 5 seconds, redirect to login
          setTimeout(() => {
            if (mounted) {
              subscription.unsubscribe();
              navigate('/customer/login', { replace: true });
            }
          }, 5000);
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        if (mounted) setError(err.message);
      }
    };

    handleAuth();
    return () => { mounted = false; };
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-500 mb-4">Login failed: {error}</p>
          <button
            onClick={() => navigate('/customer/login')}
            className="px-4 py-2 bg-black text-white rounded-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return <PageLoader />;
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <CustomerProvider>
      <div className="App">
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Navigate to="/customer/deliveries" />} />
          
          {/* Admin Routes */}
          <Route path="/sys-x9k3m-auth" element={<AdminLogin />} />
          <Route path="/sys-v4h8n-panel" element={<AdminDashboard />} />
          <Route path="/sys-r7b3c-partners" element={<ManagePartners />} />
          <Route path="/sys-c8l2d-coupons" element={<ManageCoupons />} />
          <Route path="/sys-o3z7p-orders" element={<AdminOrdersEnhanced />} />
          <Route path="/sys-a2n5k-analytics" element={<AdminAnalytics />} />
          <Route path="/sys-o3z7p-orders-legacy" element={<OrdersManagement userRole="admin" />} />
          <Route path="/sys-m5r9t-restaurants" element={<ManageRestaurants />} />
          <Route path="/sys-menu/:restaurantId" element={<ManageMenuItems />} />
          <Route path="/restaurant/:restaurantId" element={<RestaurantMenuPage />} />
          <Route path="/menu-item/:itemId" element={<MenuItemDetailPage />} />
          
          {/* Customer Routes */}
          <Route path="/customer/login" element={<CustomerLogin />} />
          <Route path="/customer/register" element={<CustomerRegister />} />
          <Route path="/customer/dashboard" element={<AuthCallback />} />
          <Route path="/customer/foods" element={<FoodsPage />} />
          <Route path="/customer/foods/:restaurantId" element={<RestaurantMenuPage />} />
          <Route path="/customer/deliveries" element={<DeliveriesPage />} />
          <Route path="/customer/edit-profile" element={<EditProfile />} />
          <Route path="/customer/update-profile" element={<UpdateProfile />} />
          <Route path="/customer/search" element={<SearchPage />} />
          <Route path="/customer/wishlist" element={<WishlistPage />} />
          <Route path="/customer/cart" element={<CartPage />} />
          <Route path="/customer/orders" element={<CustomerOrders />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/product/:type/:id" element={<ProductDetailPage />} />
          <Route path="/view-all" element={<ViewAllPage />} />
          <Route path="/fastfoods" element={<FastFoodsPage />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/travel" element={<TravelPage />} />
          
          {/* APK Download */}
          <Route path="/download" element={<DownloadPage />} />
          
          {/* Partner Routes */}
          <Route path="/dlv-q5n8t-auth" element={<PartnerLogin />} />
          <Route path="/dlv-w2r6e-panel" element={<PartnerDashboard />} />
          <Route path="/dlv-j9m1a-orders" element={<OrdersManagement userRole="partner" />} />

          {/* Restaurant Owner Portal Routes */}
          <Route path="/restaurant-portal/*" element={
            <RestaurantAuthProvider>
              <Routes>
                <Route path="login" element={<RestaurantLogin />} />
                <Route path="dashboard" element={<RestaurantPortalDashboard />} />
                <Route path="*" element={<Navigate to="/restaurant-portal/login" replace />} />
              </Routes>
            </RestaurantAuthProvider>
          } />
        </Routes>
        </Suspense>
        {/* Mandatory delivery-address selector for logged-in customers */}
        <AddressGate />
      </div>
      <Analytics />
      </CustomerProvider>
    </Router>
  );
}

export default App;